import axios from "axios";

// S5.1 — maparea Privacy a unui formular public (`consent_json.privacy`). Serverul
// rămâne sursa de adevăr: opțiunile (scopuri active, notice-uri publicate, cerințe
// per renderer) vin din `privacy-options`, iar salvarea revalidează totul.
export const NOTICE_LOCALES = Object.freeze(["ro", "en", "ru", "de"]);
export const PRIVACY_STATUS_COLORS = Object.freeze({ configured: "success", incomplete: "warning", unconfigured: undefined });
// Codurile stabile întoarse de server la salvare (au text localizat în UI).
export const PRIVACY_ISSUE_CODES = Object.freeze([
  "privacy_form_purpose_unavailable",
  "privacy_form_channel_incompatible",
  "privacy_form_doi_phone_unsupported",
  "privacy_form_notice_unavailable",
  "privacy_form_notice_mismatch",
  "privacy_form_config_required",
]);

export const fetchExternalFormPrivacyOptions = () =>
  axios.get("/ncrm/workflows/external-form-widgets/privacy-options");

// Badge-urile salvate se recalculează pe server după o schimbare a catalogului (de
// ex. un notice republicat retrage versiunea mapată pe mai multe formulare). Se
// actualizează numai starea: formularul editat, poate nesalvat, rămâne neatins.
// `fetchWidgets` este `fetchExternalFormWidgets`, primit din apelant: singura sursă
// pentru URL/parametri, fără import circular (acel modul importă deja acest helper).
export const refreshPrivacyStatuses = async (widgets, fetchWidgets) => {
  try {
    const { data } = await fetchWidgets();
    const fresh = new Map((data?.widgets || []).map((widget) => [Number(widget.id), widget]));
    for (const widget of widgets) {
      const match = fresh.get(Number(widget.id));
      if (match) Object.assign(widget, { privacy_status: match.privacy_status, privacy_issue: match.privacy_issue });
    }
  } catch {
    // Badge-urile rămân cele vechi; secțiunea editată arată oricum starea live.
  }
};

const positiveId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

// Forma editabilă din formular; lipsa configurării devine un draft gol.
export const privacyDraft = (value) => ({
  purpose_id: positiveId(value?.purpose_id),
  channel_code: value?.channel_code || null,
  notice_version_ids: Object.fromEntries(
    NOTICE_LOCALES.map((locale) => [locale, positiveId(value?.notice_version_ids?.[locale])])
  ),
});

// Payloadul trimis serverului: draftul gol devine `null` (fără config), iar
// locale-urile nemapate nu se trimit.
export const privacyPayload = (draft) => {
  const noticeVersionIds = Object.fromEntries(
    Object.entries(draft?.notice_version_ids || {}).filter(([, id]) => positiveId(id))
  );
  if (!positiveId(draft?.purpose_id) && !draft?.channel_code && !Object.keys(noticeVersionIds).length) return null;
  return {
    purpose_id: positiveId(draft?.purpose_id),
    channel_code: draft?.channel_code || null,
    notice_version_ids: noticeVersionIds,
  };
};

export const rendererRequirements = (options, rendererKey) =>
  options?.renderers?.[rendererKey] || options?.renderers?.["contact-popup"] || { locales: [], channels: [] };

// Canalele configurabile = canalele scopului ∩ valorile colectate de renderer; cu
// `field` (S5.3B), ∩ canalele livrabile pe acel câmp de contact.
export const eligibleChannels = (options, rendererKey, purposeId, field = null) => {
  const purpose = options?.purposes?.find((item) => item.id === purposeId);
  if (!purpose) return [];
  const requirements = rendererRequirements(options, rendererKey);
  const channels = field ? requirements.fields?.[field] || [] : requirements.channels;
  return channels.filter((channel) => purpose.channels.includes(channel));
};

// `GDPR-089`: confirmarea DOI pleacă numai pe email; pe un scop cu DOI, canalele telefonice
// rămân vizibile, dar nu se pot alege (serverul refuză oricum `privacy_form_doi_phone_unsupported`).
export const doiBlocksChannel = (purpose, channel) =>
  Boolean(purpose?.double_opt_in_enabled && channel && channel !== "email");

export const noticesFor = (options, purposeId, locale) =>
  (options?.notices || []).filter((notice) => notice.purpose_id === purposeId && notice.locale === locale);

// Linkuri contextuale spre proprietarul canonic, cu întoarcere în editor.
export const privacyNoticesRoute = ({ purposeId, locale, returnTo }) => ({
  path: "/ncrm/data-protection",
  query: {
    section: "notices",
    ...(purposeId ? { purpose: String(purposeId) } : {}),
    ...(locale ? { locale } : {}),
    ...(returnTo ? { returnTo } : {}),
  },
});
